import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { ShoppingCart, LogOut, LayoutDashboard, Package, Bell } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const approved = user && (user.role === "admin" || (user.role === "retailer" && user.status === "approved"));
  const [notifCount, setNotifCount] = useState(0);
  useEffect(() => {
    if (!approved) { setNotifCount(0); return; }
    const load = () => api.get("/retailer/notifications").then(({ data }) => setNotifCount(data.length)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [approved]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/catalog", label: "Products" },
    { to: "/apply", label: "Trade Account" },
  ];

  return (
    <header className="mc-glass sticky top-0 z-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="M&C Ireland" className="h-12 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${pathname === l.to ? "text-[#D4AF37]" : "text-slate-300 hover:text-[#D4AF37]"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {approved && (
            <>
              <Link to="/portal" data-testid="nav-portal"
                className="hidden rounded-full border border-[#2A2E39] px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37] sm:inline-flex">
                <Package className="mr-1.5 h-4 w-4" /> Portal
              </Link>
              {user.role === "retailer" && (
                <Link to="/portal/alerts" data-testid="nav-alerts"
                  className="relative rounded-full border border-[#2A2E39] p-2.5 text-slate-200 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
                  <Bell className="h-4 w-4" />
                  {notifCount > 0 && (
                    <span data-testid="nav-alerts-count" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D4AF37] px-1 text-[11px] font-bold text-black">{notifCount}</span>
                  )}
                </Link>
              )}
              <Link to="/portal/cart" data-testid="nav-cart"
                className="relative rounded-full border border-[#2A2E39] p-2.5 text-slate-200 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
                <ShoppingCart className="h-4 w-4" />
                {count > 0 && (
                  <span data-testid="nav-cart-count" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D4AF37] px-1 text-[11px] font-bold text-black">{count}</span>
                )}
              </Link>
            </>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className="hidden rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm font-medium text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 sm:inline-flex">
              <LayoutDashboard className="mr-1.5 h-4 w-4" /> Admin
            </Link>
          )}
          {user && user.role === "retailer" && user.status !== "approved" && (
            <Link to="/portal/pending" data-testid="nav-pending" className="text-sm text-amber-400">Pending</Link>
          )}
          {user ? (
            <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }}
              className="rounded-full border border-[#2A2E39] p-2.5 text-slate-300 transition-colors hover:border-rose-500/50 hover:text-rose-400" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login" className="mc-btn-gold rounded-full px-5 py-2 text-sm">Trade Login</Link>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      {children}
      <Footer />
    </div>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#2A2E39] bg-[#0B0C0E]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-4">
            <img src={LOGO_URL} alt="M&C Ireland" className="h-16 w-auto" />
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#94A3B8]">
              Ireland's trusted importer and wholesale distributor of world foods. Approved trade partners order
              with live, Sage-verified availability.
            </p>
          </div>
          <div className="md:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37]">Explore</h4>
            <ul className="mt-4 space-y-2 text-sm text-[#94A3B8]">
              <li><Link to="/catalog" className="hover:text-[#D4AF37]">Product Catalogue</Link></li>
              <li><Link to="/apply" className="hover:text-[#D4AF37]">Apply for Trade Account</Link></li>
              <li><Link to="/login" className="hover:text-[#D4AF37]">Trade Login</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37]">Contact Number</h4>
            <ul className="mt-4 space-y-2 text-sm text-[#94A3B8]">
              <li>Landline: <a href="tel:+35318807982" className="hover:text-[#D4AF37]">+353 188 07982</a></li>
              <li>Mobile: <a href="tel:+353899661800" className="hover:text-[#D4AF37]">+353 89 966 1800</a></li>
              <li><a href="mailto:order@mandc.ie" className="hover:text-[#D4AF37]">order@mandc.ie</a></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37]">HQ Office</h4>
            <p className="mt-4 text-sm leading-relaxed text-[#94A3B8]">
              Unit 7, The Business Centre, 618 Northwest Logistics Park, Ballycoolin, Dublin 15, Ireland
              <br />D15 V00Y
            </p>
          </div>
        </div>
        <div className="mt-10 border-t border-[#2A2E39] pt-6 text-xs text-[#64748B]">
          © 2026 M&C Ireland. Trade pricing & stock available to approved retailers only.
        </div>
      </div>
    </footer>
  );
}
